import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import update from 'immutability-helper'
import Dropzone from 'react-dropzone'

import FilePreview from './FilePreview'
import SendMessageForm from './SendMessageForm'
import MessageFile from './MessageFile'
import MessageImage from './MessageImage'

class MessageFormContainer extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      attachments: {},
      message: '',
      isMessageInputRestricted: true,
      isBeingSent: false,
      isDropzoneActive: false,
      rows: 1,
      minRows: 1,
      maxRows: 5,
      iAmTyping: false,
    }

    this.timer = null

    this.handleFileUpload = this.handleFileUpload.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.submitSuccess = this.submitSuccess.bind(this)
    this.submitFail = this.submitFail.bind(this)
    this.onFileUploadRemove = this.onFileUploadRemove.bind(this)
    this.handleMessageInput = this.handleMessageInput.bind(this)
    this.onDrop = this.onDrop.bind(this)
    this.updateDimensions = this.updateDimensions.bind(this)
    this.updateTypingState = this.updateTypingState.bind(this)
  }

  componentDidMount() {
    let counter = 0

    window.addEventListener('dragenter', () => {
      this.setState({
        isDropzoneActive: true,
        isMessageInputRestricted: false,
      })
      counter++
    })

    window.addEventListener('dragleave', () => {
      counter--
      if (counter === 0) {
        this.setState({
          isDropzoneActive: false,
        })
      }
    })

    window.addEventListener('drop', () => {
      counter = 0
      this.setState({
        isDropzoneActive: false,
      })
    })

    window.addEventListener('resize', this.updateDimensions)
    this.updateDimensions()
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions)
    clearTimeout(this.timer)
  }

  updateDimensions() {
    const winHeight = window.innerHeight
    let textareaHeight = 0

    if (window.innerWidth <= 920) {
      textareaHeight = winHeight * 0.35 - 44
    } else {
      textareaHeight = winHeight * 0.40
    }
    textareaHeight = Math.ceil(textareaHeight / 22)

    this.setState({
      maxRows: textareaHeight,
      rows: this.state.rows < textareaHeight ? this.state.rows : textareaHeight,
    })
  }

  handleFileUpload(e, skip = false) {
    let currentAttachments = []

    const fileSizeLimit = this.props.isJobConversation ? 500 : 100
    const calculatedSizeLimit = fileSizeLimit * 1024 * 1024

    if (skip === true) {
      currentAttachments = e
    } else {
      [...e.target.files].forEach((file) => {
        if (file.size > calculatedSizeLimit) {
          App.events.publish(
            'app:notification:flash:error',
            `Attachment is too big, please try to not exceed ${fileSizeLimit}MBs.`
          )
          return
        } if (this.state.attachments.hasOwnProperty(file.name)) {
          App.events.publish(
            'app:notification:flash:error',
            'You cannot attach file with the same filename'
          )
          return
        }

        currentAttachments.push(file)
      })
    }

    const newAttachmentsState = {}
    currentAttachments.forEach((file) => {
      newAttachmentsState[file.name] = {
        id: null,
        name: file.name,
        progress: 0,
        type: file.type,
        size: file.size,
        isUploaded: false,
      }
    })

    this.setState({
      isMessageInputRestricted: false,
      attachments: update(this.state.attachments, {
        $merge: newAttachmentsState,
      }),
    })

    const isIncreasedSizeLimit = this.props.isJobConversation

    currentAttachments.forEach((file) => {
      const data = new FormData()
      data.append('file', file)
      data.append('increased_size_limit', isIncreasedSizeLimit)

      axios.post('/attachments', data, { onUploadProgress: this.onUploadProgress.bind(this, file.name) })
        .then(response => this.uploadSuccess(file.name, response))
        .catch(error => this.uploadFail(file.name, error))
    })
  }

  uploadSuccess(fileName, response) {
    if (this.state.attachments[fileName]) {
      this.setState({
        attachments: update(this.state.attachments, {
          [fileName]: {
            $merge: {
              id: response.data[0].id,
              preview: response.data[0].url,
              isUploaded: true,
            },
          },
        }),
      })
    } else {
      axios.delete(`/attachments/${response.data[0].id}`).catch((error) => {})
    }
  }

  uploadFail(fileName, error = {}) {
    const { data } = error.response || { data: { errors: [] } }
    const errMsg = data.errors.join(', ')

    App.events.publish(
      'app:notification:flash:error',
      errMsg || 'Your file failed to attach. Please try again or contact our support'
    )

    this.setState({
      attachments: update(this.state.attachments, {
        $unset: [fileName],
      }),
    })
  }

  onUploadProgress(fileName, progressEvent) {
    if (!fileName) { return }

    const uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total) || 0
    // eslint-disable-next-line no-console
    console.log(this.state.attachments)
    // eslint-disable-next-line no-console
    console.log(fileName)

    this.setState({
      attachments: update(this.state.attachments || {}, {
        [fileName]: { $merge: { progress: uploadProgress } },
      }),
    })
  }

  handleSubmit(e) {
    e.preventDefault()

    this.setState({
      isBeingSent: true,
    })

    axios.post(`/api/internal/conversations/${this.props.conversationId}/messages`, {
      conversation_message: this.state.message,
      attachment_ids: Object.values(this.state.attachments).map((file) => file.id),
    })
      .then(this.submitSuccess)
      .catch(this.submitFail)
  }

  submitSuccess(response) {
    this.props.onSubmit(response)

    this.setState({
      message: '',
      attachments: {},
      isBeingSent: false,
    })
  }

  submitFail(xhr, status, err) {
    this.setState({
      isBeingSent: false,
    })

    let message = 'Your message failed to be sent. Please try again or contact our support'

    if (xhr.response.data.errors) {
      message = xhr.response.data.errors[0].detail
    }

    App.events.publish('app:notification:flash:error', message)
  }

  onFileUploadRemove(fileName) {
    const removableAttachmentId = this.state.attachments[fileName].id

    if (removableAttachmentId) {
      axios.delete(`/attachments/${removableAttachmentId}`).catch((error) => {})
    }

    this.setState({
      attachments: update(this.state.attachments, {
        $unset: [fileName],
      }),
    })
  }

  handleMessageInput(event) {
    const previousRows = event.target.rows

    event.target.rows = this.state.minRows
    const currentRows = Math.round((event.target.scrollHeight - 18) / 22)

    if (currentRows === previousRows) {
      event.target.rows = currentRows
    }
    if (currentRows >= this.state.maxRows) {
      event.target.rows = this.state.maxRows
    }

    if (this.props.isLiveMessaging && !this.state.iAmTyping) {
      this.props.updateTypingState(true)
    }

    this.setState({
      message: event.target.value,
      isMessageInputRestricted: !(event.target.value.length > 0),
      rows: currentRows < this.state.maxRows ? currentRows : this.state.maxRows,
      iAmTyping: true,
    })

    clearTimeout(this.timer)
    this.timer = setTimeout(this.updateTypingState, 1000)
  }

  updateTypingState() {
    this.setState({ iAmTyping: false })

    if (this.props.isLiveMessaging) {
      this.props.updateTypingState(false)
    }
  }

  onDrop(files) {
    this.setState({
      isDropzoneActive: false,
      isMessageInputRestricted: false,
    })
    this.handleFileUpload(files, true)
  }

  render() {
    const FileUploads = Object.values(this.state.attachments).map((attachment, i) => {
      const dataWrap = {
        attributes: {
          url: attachment.preview,
          fileSize: attachment.size,
          fileName: attachment.name,
        },
      }

      const isImage = attachment.type.indexOf('image') >= 0

      return (
        <div className="attachment__column col-xs-12 col-sm-6 col-md-4 col-lg-3" key={`attachment-wrap-${i}`}>
          <div className="attachment__wrapper">
            <FilePreview
              progress={attachment.progress}
              fileName={attachment.name}
              iconName={isImage ? 'image' : 'file-archive'}
              imgUrl={attachment.preview}
              isProcessing={attachment.progress === 100 && !attachment.isUploaded}
            />
            {isImage ? (
              <MessageImage image={dataWrap} iconName="times" onImageClick={this.onFileUploadRemove} />
            ) : (
              <MessageFile file={dataWrap} iconName="times" removeFile={this.onFileUploadRemove} />
            )}
          </div>
        </div>
      )
    })

    const isDisabledSubmit = Object.values(this.state.attachments).filter((attachment) => attachment.id === null).length > 0
            || this.state.isBeingSent

    if (this.props.isAttachmentsHidden) {
      return (
        <div>
          <SendMessageForm
            handleSubmit={this.handleSubmit}
            handleMessageInput={this.handleMessageInput}
            isDisabledSubmit={isDisabledSubmit}
            isSuspendedMessaging={this.props.isSuspendedMessaging}
            message={this.state.message}
          />
        </div>
      )
    }
    return (
      <Dropzone
        disableClick
        className="attach-dropzone"
        onDrop={this.onDrop}
      >
        {this.state.isDropzoneActive && (
        <div className="attach-dropzone__container">
          <div className="attach-dropzone__text-box">Drop Files Here</div>
        </div>
        )}
        <SendMessageForm
          handleFileUpload={this.handleFileUpload}
          handleSubmit={this.handleSubmit}
          handleMessageInput={this.handleMessageInput}
          isDisabledSubmit={isDisabledSubmit}
          message={this.state.message}
          isSuspendedMessaging={this.props.isSuspendedMessaging}
          showAttachments
          attachments={FileUploads}
          isRestricted={this.state.isMessageInputRestricted}
          rows={this.state.rows}
        />
      </Dropzone>
    )
  }
}

MessageFormContainer.propTypes = {
  conversationId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSuspendedMessaging: PropTypes.bool.isRequired,
  isAttachmentsHidden: PropTypes.bool.isRequired,
  isLiveMessaging: PropTypes.bool.isRequired,
  updateTypingState: PropTypes.func,
  isJobConversation: PropTypes.bool,
}

MessageFormContainer.defaultProps = {
  updateTypingState: () => {},
  isJobConversation: false,
}

export default MessageFormContainer
